<template>
  <v-container>
    <Title title="Qualification Details"></Title>

    <h2 class="py-5" style="display: inline-block">{{ term }} Details</h2>
    <BulkUploader :type="'qualification'" style="float: right" />
    <v-stepper v-model="e6" vertical>
      <v-stepper-step color="#6490f1" :complete="e6 > 1" step="1" editable
        >{{ term }} Info</v-stepper-step
      >
      <v-stepper-content step="1">
        <v-form ref="detailsForm">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                ref="firstInput"
                v-model="unitName"
                :label="modifiedNameLabel"
                class="my-3"
                :rules="emptyRule"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="unitCode"
                :label="modifiedCodeLabel"
                class="my-3"
                :rules="emptyRule"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" v-if="role != 'centre'">
              <v-text-field
                v-model="qan"
                label="QAN"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedUnitType"
                :items="modifiedUnitTypes"
                :label="modifiedTypeLabel"
                :rules="emptyRule"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedUnitLevel"
                :items="unitLevels"
                :label="modifiedLevelLabel"
                :rules="emptyRule"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedUnitStatus"
                :items="unitStatus"
                :label="modifiedStatusLabel"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4" v-if="role != 'centre'">
              <v-text-field
                v-model="unitCredits"
                type="number"
                label="Qualification Credits *"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="unitNote"
                :label="modifiedNoteLabel"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                type="number"
                v-model="mandatoryUnitNumber"
                label="No. Mandatory Units *"
                :rules="emptyRule"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-combobox
                v-model="selectedMandatoryUnits"
                :items="unitOptions"
                chips
                clearable
                label="Mandatory Units *"
                :rules="comboboxRule"
                hide-selected
                multiple
              >
                <template v-slot:selection="{ attrs, item, select, selected }">
                  <v-chip
                    small
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    @click="select"
                    @click:close="removeMandatoryUnit(item)"
                  >
                    <strong>{{ item }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                type="number"
                v-model="optionalUnitNumber"
                label="No. Optional Units"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-combobox
                v-model="selectedOptionalUnits"
                :items="unitOptions"
                chips
                clearable
                label="Optional Units"
                hide-selected
                multiple
              >
                <template v-slot:selection="{ attrs, item, select, selected }">
                  <v-chip
                    small
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    @click="select"
                    @click:close="removeOptionalUnit(item)"
                  >
                    <strong>{{ item }}</strong
                    >&nbsp;
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                ref="startMenu"
                v-model="startMenu"
                :close-on-content-click="false"
                :return-value.sync="startDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="modifiedStartDateLabel"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    class="my-3"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  scrollable
                  @input="$refs.startMenu.save(startDate)"
                >
                  <v-spacer></v-spacer>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="4">
              <v-menu
                ref="reviewMenu"
                v-model="reviewMenu"
                :close-on-content-click="false"
                :return-value.sync="reviewDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="reviewDate"
                    :label="modifiedReviewDateLabel"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    class="my-3"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="reviewDate"
                  no-title
                  scrollable
                  @input="$refs.reviewMenu.save(reviewDate)"
                >
                  <v-spacer></v-spacer>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="certificationTemplate"
                :items="certificationTemplates"
                label="Certification Template"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="tqt"
                type="number"
                :label="modifiedTotalQualificationTimeLabel"
                :rules="emptyRule"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedCriterion"
                :items="modifiedGradingCriterions"
                :rules="emptyRule"
                label="Grading Criterion *"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4" v-if="role != 'centre'">
              <v-select
                v-model="selectedLanguage"
                :items="gradingLanguages"
                label="Grading Language"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="minLearningHours"
                type="number"
                label="Minimum Guided Learning Hours  *"
                :rules="emptyRule"
                class="my-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedNoOfResits"
                :items="noOfResits"
                label="Number of Resits"
                class="my-3"
              ></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-combobox
                v-model="selectedDeliveryMode"
                :items="deliveryModes"
                clearable
                label="Delivery Mode *"
                multiple
                class="my-3"
              ></v-combobox>
            </v-col>
          </v-row>
        </v-form>
        <v-btn tile color="#6490f1" dark @click="onSubmitDetails()"
          >Continue</v-btn
        >
        <div class="mb-5"></div>
      </v-stepper-content>

      <v-stepper-step color="#6490f1" :complete="e6 > 2" step="2" editable
        >{{ term }} Descriptions</v-stepper-step
      >
      <v-stepper-content step="2">
        <v-row class="mb-10">
          <v-col cols="12">
            <template v-if="isDescriptionLoaded">
              <Editor
                :description="unitDescription"
                @updateContent="updateContent"
              />
            </template>
          </v-col>
        </v-row>
        <div class="my-12"></div>
        <v-btn tile color="#6490f1" dark @click="e6 = 3" class="mt-6"
          >Continue</v-btn
        >
        <v-btn tile text @click="e6 = 1" class="mt-6">Previous</v-btn>
        <div class="mb-5"></div>
      </v-stepper-content>

      <v-stepper-step color="#6490f1" :complete="e6 > 3" step="3" editable
        >{{ term }} Materials</v-stepper-step
      >
      <v-stepper-content step="3">
        <v-form class="mt-2">
          <v-row>
            <v-col cols="12" md="12">
              <p>
                {{ term }} Image (File Format: JPEG, PNG)
                <input
                  type="file"
                  accept=".jpg, .jpeg, .png"
                  @change="onAddFile($event, 0)"
                />
              </p>
              <div class="preview mb-12">
                <embed v-if="imageUrl" :src="imageUrl" />
              </div>
            </v-col>
            <v-col cols="12" md="12">
              <p>
                {{ term }} Video (File Format: MP4)
                <input
                  type="file"
                  accept=".mp4"
                  @change="onAddFile($event, 1)"
                />
              </p>
              <div class="preview mb-12">
                <embed v-if="videoUrl" :src="videoUrl" />
              </div>
            </v-col>
            <v-col cols="12" md="12">
              <p>
                {{ term }} Brochure (File Format: JPEG, PDF)
                <input
                  type="file"
                  accept=".jpg, .jpeg, .png, .pdf"
                  @change="onAddFile($event, 2)"
                />
              </p>
              <div class="preview mb-12">
                <embed v-if="brochureUrl" :src="brochureUrl" />
              </div>
            </v-col>
            <v-col cols="12" md="12">
              <p>
                {{ term }} Guide (File Format: PDF)
                <input
                  type="file"
                  accept=".pdf"
                  @change="onAddFile($event, 3)"
                />
              </p>
              <div class="preview mb-12">
                <embed v-if="guideUrl" :src="guideUrl" />
              </div>
            </v-col>
          </v-row>
        </v-form>
        <v-btn tile color="#6490f1" dark @click="e6 = 4">Continue</v-btn>
        <v-btn tile text @click="e6 = 2">Previous</v-btn>
        <div class="mb-5"></div>
      </v-stepper-content>

      <v-stepper-step color="#6490f1" :complete="e6 > 4" step="4" editable
        >Learning Outcomes</v-stepper-step
      >
      <v-stepper-content step="4">
        <v-form>
          <v-row>
            <v-col cols="12" md="12">
              <v-textarea
                name="input-7-1"
                label="Description"
                class="my-3"
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="12">
              <v-text-field
                label="How will it be assessed?"
                class="my-3"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
        <v-btn tile color="#6490f1" dark @click="onReviewSummary()"
          >Review summary</v-btn
        >
        <v-btn tile text @click="e6 = 3">Previous</v-btn>
        <div class="mb-5"></div>
      </v-stepper-content>
    </v-stepper>
  </v-container>
</template>

<script>
import Editor from "../components/Editor";
import BulkUploader from "../components/BulkUploader";
import Title from "../components/Title";

export default {
  mounted: async function () {
    if (this.$store.state.role == "centre") {
      await this.getEndorsedUnits();
    } else {
      await this.getUnits();
    }

    this.getUnitTypes();
    this.getUnitLevels();
    this.getUnitStatuses();
    this.getGradingCriterions();
    this.getLanguages();
    this.getQualification();
    this.getCertificationTemplates();

    this.$refs.firstInput.focus();

    if (this.$route.query.id) {
      this.isDescriptionLoaded = false;
    }
  },
  components: {
    Editor,
    BulkUploader,
    Title,
  },
  data() {
    return {
      role: this.$store.state.role,
      qualificationTerm: "Qualification",
      units: [],
      unitsName: [],
      mandatoryUnitNumber: null,
      mandatoryUnits: [],
      selectedMandatoryUnitsId: [],
      selectedMandatoryUnits: [],
      optionalUnitNumber: null,
      optionalUnits: [],
      selectedOptionalUnitsId: [],
      selectedOptionalUnits: [],
      e6: 1,
      startMenu: false,
      reviewMenu: false,
      certificationTemplates: [],
      deliveryModes: ["Online", "Blended", "Face to Face"],
      noOfResits: [0, 1, 2, 3],
      unitTypes: [],
      unitLevels: [],
      unitStatus: [],
      gradingCriterions: [],
      gradingLanguages: [],
      unitName: null,
      unitCode: null,
      qan: null,
      unitDescription: null,
      selectedUnitType: null,
      selectedUnitLevel: null,
      unitCredits: null,
      selectedUnitStatus: null,
      unitNumber: null,
      unitNote: null,
      startDate: null,
      reviewDate: null,
      certificationTemplate: null,
      tqt: null,
      selectedCriterion: null,
      selectedLanguage: null,
      minLearningHours: null,
      selectedNoOfResits: 1,
      selectedDeliveryMode: [],
      unitImage: null,
      unitVideo: null,
      unitBrochure: null,
      unitGuide: null,
      emptyRule: [(v) => !!v || "This field is required"],
      comboboxRule: [(v) => v.length > 0 || "This field is required"],
      isDescriptionLoaded: true,
      imageUrl: null,
      videoUrl: null,
      brochureUrl: null,
      guideUrl: null,
      nameLabel: "Qualification Name *",
      codeLabel: "Qualification Code *",
      typeLabel: "Qualification Type *",
      levelLabel: "Qualification Level *",
      statusLabel: "Qualification Status",
      noteLabel: "Qualification Note",
      startDateLabel: "Qualification Start Date",
      reviewDateLabel: "Qualification Review Date",
      totalQualificationTimeLabel: "Total Course Time *",
    };
  },
  computed: {
    modifiedNameLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Name *";
      }

      return this.nameLabel;
    },
    modifiedCodeLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Code *";
      }

      return this.codeLabel;
    },
    modifiedTypeLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Type *";
      }

      return this.typeLabel;
    },
    modifiedLevelLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Level *";
      }

      return this.levelLabel;
    },
    modifiedStatusLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Status";
      }

      return this.statusLabel;
    },
    modifiedNoteLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Note";
      }

      return this.noteLabel;
    },
    modifiedStartDateLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Start Date";
      }

      return this.startDateLabel;
    },
    modifiedReviewDateLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Course Review Date";
      }

      return this.reviewDateLabel;
    },
    modifiedTotalQualificationTimeLabel: function () {
      if (this.$store.state.role == "centre") {
        return "Total Course Time *";
      }

      return this.totalQualificationTimeLabel;
    },
    term: function () {
      if (this.$store.state.role == "centre") {
        return "Course";
      }

      return this.qualificationTerm;
    },
    imageTitle: function () {
      if (this.unitImage) {
        return this.unitImage.name;
      }
      return null;
    },
    videoTitle: function () {
      if (this.unitVideo) {
        return this.unitVideo.name;
      }
      return null;
    },
    brochureTitle: function () {
      if (this.unitBrochure) {
        return this.unitBrochure.name;
      }
      return null;
    },
    guideTitle: function () {
      if (this.unitGuide) {
        return this.unitGuide.name;
      }
      return null;
    },
    unitOptions: function () {
      return this.unitsName.filter((unit) => {
        return (
          !this.selectedMandatoryUnits.includes(unit) &&
          !this.selectedOptionalUnits.includes(unit)
        );
      });
    },
    modifiedUnitTypes: function () {
      if (this.role == "centre") {
        return this.unitTypes.filter((type) => {
          return type.toLowerCase() == "endorsed";
        });
      }

      return this.unitTypes;
    },
    modifiedGradingCriterions: function () {
      if (this.role == "centre") {
        return this.gradingCriterions.filter((criterion) => {
          return criterion.toLowerCase() == "status";
        });
      }

      return this.gradingCriterions;
    },
  },
  methods: {
    qualificationItem: function () {
      return {
        name: this.unitName,
        code: this.unitCode,
        qan: this.qan,
        description: this.unitDescription,
        type: this.selectedUnitType,
        level: this.selectedUnitLevel,
        credits: this.unitCredits,
        status: this.selectedUnitStatus,
        number: this.unitNumber,
        note: this.unitNote,
        noOfMandatoryUnits: this.mandatoryUnitNumber,
        mandatoryUnits: this.selectedMandatoryUnits,
        noOfOptionalUnits: this.optionalUnitNumber,
        optionalUnits: this.selectedOptionalUnits,
        startDate: this.startDate,
        reviewDate: this.reviewDate,
        certificationTemplate: this.certificationTemplate,
        tqt: this.tqt,
        gradingCriterion: this.selectedCriterion,
        gradingLanguage: this.selectedLanguage,
        minimumHour: this.minLearningHours,
        noOfResits: this.selectedNoOfResits,
        deliveryMode: this.selectedDeliveryMode,
        image: this.unitImage,
        video: this.unitVideo,
        brochure: this.unitBrochure,
        guide: this.unitGuide,
        units: this.units,
      };
    },
    writeToForm: function (item) {
      const selectedMandatory = [];
      const selectedOptional = [];

      this.unitName = item.name;
      this.unitCode = item.code;
      this.qan = item.qan;
      this.unitDescription = item.description;
      this.selectedUnitType =
        item.type.charAt(0).toUpperCase() + item.type.slice(1);
      this.selectedUnitLevel = item.level;
      this.unitCredits = item.credits;
      this.selectedUnitStatus = item.status;
      this.unitNote = item.note;
      this.mandatoryUnitNumber = item.noOfMandatoryUnits;
      this.mandatoryUnits = item.mandatoryUnits;
      this.optionalUnitNumber = item.noOfOptionalUnits;
      this.optionalUnits = item.optionalUnits;
      this.UnitNumber = item.optionalUnits;
      this.startDate = item.startDate;
      this.reviewDate = item.reviewDate;
      this.certificationTemplate = item.certificationTemplate;
      this.tqt = item.tqt;
      this.selectedCriterion = item.gradingCriterion;
      this.selectedLanguage = item.gradingLanguage;
      this.minLearningHours = item.minimumHour;
      this.selectedNoOfResits = item.noOfResits;
      this.selectedDeliveryMode = item.deliveryMode;
      this.unitImage = item.image;
      this.unitVideo = item.video;
      this.unitBrochure = item.brochure;
      this.unitGuide = item.guide;
      this.isDescriptionLoaded = true;

      // retrive mandatory unit name from id
      for (let i = 0; i < this.mandatoryUnits.length; i++) {
        for (let j = 0; j < this.units.length; j++) {
          if (this.units[j].id == this.mandatoryUnits[i]) {
            selectedMandatory.push(this.units[j].code);
          }
        }
      }

      // retrive optional unit name from ID
      for (let i = 0; i < this.optionalUnits.length; i++) {
        for (let j = 0; j < this.units.length; j++) {
          if (this.units[j].id == this.optionalUnits[i]) {
            selectedOptional.push(this.units[j].code);
          }
        }
      }

      this.selectedMandatoryUnits = selectedMandatory;
      this.selectedOptionalUnits = selectedOptional;
    },
    getQualification: function () {
      if (this.$route.params.item) {
        // edit new qualification
        this.writeToForm(this.$route.params.item);
      } else if (this.$route.query.id) {
        // edit exisiting qualification
        fetch(
          `${process.env.VUE_APP_ROOT_API}/qualifications/${this.$route.query.id}`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.jwt}`,
            },
          }
        )
          .then((response) => response.json())
          .then((data) => {
            this.writeToForm(data.qualification);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    getEndorsedUnits: function () {
      const promise = fetch(`${process.env.VUE_APP_ROOT_API}/units/endorsed`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.units = data.units.map((unit) => {
            return { code: unit.code, id: unit._id };
          });

          this.mandatoryUnits = data.units.map((unit) => {
            return { name: unit.name, id: unit._id };
          });

          this.optionalUnits = data.units.map((unit) => {
            return { name: unit.name, id: unit._id };
          });

          this.unitsName = data.units.map((unit) => unit.code);
        })
        .catch((error) => {
          console.error(error);
        });

      return promise;
    },
    getUnits: function () {
      const promise = fetch(`${process.env.VUE_APP_ROOT_API}/units`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.units = data.units.map((unit) => {
            return { code: unit.code, id: unit._id };
          });

          this.mandatoryUnits = data.units.map((unit) => {
            return { name: unit.name, id: unit._id };
          });

          this.optionalUnits = data.units.map((unit) => {
            return { name: unit.name, id: unit._id };
          });

          this.unitsName = data.units.map((unit) => unit.code);
        })
        .catch((error) => {
          console.error(error);
        });

      return promise;
    },
    onReviewSummary() {
      if (this.$route.query.id) {
        this.$router.push({
          name: "Summary",
          params: { section: "qualifications", item: this.qualificationItem() },
          query: { id: this.$route.query.id },
        });
      } else {
        this.$router.push({
          name: "Summary",
          params: { section: "qualifications", item: this.qualificationItem() },
        });
      }
    },
    removeMandatoryUnit(item) {
      this.selectedMandatoryUnits.splice(
        this.selectedMandatoryUnits.indexOf(item),
        1
      );
      this.selectedMandatoryUnits = [...this.selectedMandatoryUnits];
    },
    removeOptionalUnit(item) {
      this.selectedOptionalUnits.splice(
        this.selectedOptionalUnits.indexOf(item),
        1
      );
      this.selectedOptionalUnits = [...this.selectedOptionalUnits];
    },
    getUnitTypes: function () {
      fetch(`${process.env.VUE_APP_ROOT_API}/unitTypes`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.unitTypes = data.unitTypes.map((type) => type.name);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getUnitLevels: function () {
      fetch(`${process.env.VUE_APP_ROOT_API}/unitLevels`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.unitLevels = data.unitLevels.map((level) => level.name);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getUnitStatuses: function () {
      fetch(`${process.env.VUE_APP_ROOT_API}/unitStatuses`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.unitStatus = data.unitStatuses.map((status) => status.name);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getCertificationTemplates: async function () {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/templates?type=certification`,
          {
            headers: {
              Authorization: `Bearer ${this.$store.state.jwt}`,
            },
          }
        );
        const data = await response.json();

        this.certificationTemplates = data.templates.map(
          (template) => template.name
        );
      } catch (err) {
        console.log(err);
      }
    },
    getGradingCriterions: function () {
      fetch(`${process.env.VUE_APP_ROOT_API}/gradingCriterions`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.gradingCriterions = data.gradingCriterions.map(
            (criterion) => criterion.name
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getLanguages: function () {
      fetch(`${process.env.VUE_APP_ROOT_API}/languages`, {
        headers: {
          Authorization: `Bearer ${this.$store.state.jwt}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.gradingLanguages = data.languages.map(
            (language) => language.name
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    onSubmitDetails: function () {
      const validate = this.$refs.detailsForm.validate();

      if (validate) {
        this.e6 = 2;
      }
    },
    updateContent: function (content) {
      this.unitDescription = content;
    },
    onAddFile: function (e, index) {
      const file = e.target.files[0];

      if (index == 0) {
        this.imageUrl = URL.createObjectURL(file);
        this.unitImage = file;
      } else if (index == 1) {
        this.videoUrl = URL.createObjectURL(file);
        this.unitVideo = file;
      } else if (index == 2) {
        this.brochureUrl = URL.createObjectURL(file);
        this.unitBrochure = file;
      } else if (index == 3) {
        this.guideUrl = URL.createObjectURL(file);
        this.unitGuide = file;
      }
    },
  },
};
</script>

<style scoped>
* {
  padding-top: 0;
  padding-bottom: 0;
}

.form-title {
  display: inline-block;
}

#continue-btn {
  float: right;
}

#preview-container {
  height: 500px;
  overflow-y: scroll;
}

.preview {
  display: flex;
}

.preview embed {
  width: 70%;
  height: 400px;
}

/* md size */
@media (min-width: 960px) {
}

/* sm size */
@media (min-width: 600px) and (max-width: 960px) {
}

/* xs size */
@media only screen and (max-width: 600px) {
}
</style>